import React, { useState } from "react"

import { useTranslation } from "react-i18next"

import { Modal } from "components/modals/Modal"

import { AdminBonus2Simple, PromoCodeWriteFailure } from "services/bonus/type"

import { getDisplayName } from "../../helpers/bonus2.helpers"

import { PromoCodeListEditor } from "./PromoCodeListEditor"

interface Props {
  bonuses: AdminBonus2Simple[]
  tenant: string
  onSubmit: (bonusId: string, codes: string[]) => Promise<PromoCodeWriteFailure | null>
  onClose: () => void
}

export const CreatePromoCodeModal = ({ bonuses, tenant, onSubmit, onClose }: Props) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [codes, setCodes] = useState<string[]>([""])
  const [bonusId, setBonusId] = useState("")
  const [refused, setRefused] = useState<PromoCodeWriteFailure | undefined>(undefined)
  const [saving, setSaving] = useState(false)

  const filled = codes.map((c) => c.trim()).filter(Boolean)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!bonusId || filled.length === 0) return
    setSaving(true)
    const failure = await onSubmit(bonusId, filled)
    setSaving(false)
    setRefused(failure ?? undefined)
  }

  const selected = bonuses.find((b) => b.id === bonusId)

  return (
    <Modal open={true} handler={onClose}>
      <div className="bg-[#1A1A2E] rounded-lg p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <h3 className="text-lg font-bold text-white mb-4">{t("bonus2.promo.createPromoCode")}</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">{t("bonus2.promo.promoCodes")}</label>
            <PromoCodeListEditor
              codes={codes}
              onChange={setCodes}
              tenant={tenant}
              refused={refused}
              bonusName={selected ? getDisplayName(selected.name) : undefined}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">{t("bonus2.promo.linkToBonus")}</label>
            <select
              value={bonusId}
              onChange={(e) => setBonusId(e.target.value)}
              className="w-full bg-[#0C0C1A] text-white border border-gray-600 rounded px-3 py-2"
            >
              <option value="">{t("bonus2.promo.selectABonus")}</option>
              {bonuses.map((bonus) => (
                <option key={bonus.id} value={bonus.id}>
                  {getDisplayName(bonus.name)} ({bonus.type})
                  {bonus.promo_codes.length > 0 ? ` — ${t("bonus2.promo.hasCodes", { count: bonus.promo_codes.length })}` : ""}
                </option>
              ))}
            </select>
            {bonuses.length === 0 && <p className="text-yellow-500 text-xs mt-1">{t("bonus2.promo.noActiveBonuses")}</p>}
          </div>

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700">
              {t("bonus2.promo.cancel")}
            </button>
            <button
              type="submit"
              disabled={bonuses.length === 0 || filled.length === 0 || !bonusId || saving}
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? t("bonus2.promo.saving") : t("bonus2.promo.create")}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  )
}
